// Shared Slim-Select option-swap mechanic for the two faceted-cascade families
// (ADR-0059): faceted_search_controller.js and faceted_filter_controller.js.
//
// A select wired with data-controller="slim" builds its SlimSelect instance
// ~100ms after connect, so a swap that lands first has to wait for it.

// Calls onReady(slimInstance) once the element's Slim instance exists,
// retrying every `interval` ms up to `attempts` times, then giving up.
// Calls onNoSlim() straight away when the element has no slim controller
// at all (a plain <select>).
export function whenSlimReady(application, el, { onReady, onNoSlim, attempts = 10, interval = 50 }) {
  const ctrl = application.getControllerForElementAndIdentifier(el, "slim")
  if (!ctrl) {
    if (onNoSlim) onNoSlim()
    return
  }

  const tryReady = (remaining) => {
    const current = application.getControllerForElementAndIdentifier(el, "slim")
    if (current?.select) {
      onReady(current.select)
    } else if (remaining > 0) {
      setTimeout(() => tryReady(remaining - 1), interval)
    }
  }
  tryReady(attempts)
}

// Replace a select's options with `items` ([{ text, value, selected, html }]),
// through Slim when it's there (no flicker) or on the native <select>.
//
// Slim's setData dispatches a bubbling `change` on the native select. While
// the swap runs the element is flagged, so a listener can tell a swap from a
// user's change with isSwappingOptions(el).
export function swapSelectOptions(application, el, items) {
  const applyNative = () => withSwapFlag(el, () => {
    el.innerHTML = ""
    items.forEach(item => {
      el.add(new Option(item.text, item.value, false, !!item.selected))
    })
  })

  whenSlimReady(application, el, {
    onReady: (slim) => withSwapFlag(el, () => slim.setData(items)),
    onNoSlim: applyNative
  })
}

export function isSwappingOptions(el) {
  return el.dataset.slimOptionsSwapping === "true"
}

function withSwapFlag(el, fn) {
  el.dataset.slimOptionsSwapping = "true"
  try {
    fn()
  } finally {
    delete el.dataset.slimOptionsSwapping
  }
}
